import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { realtimeService } from '@/services/realtimeService';
import { queryKeys } from '@/lib/queryKeys';
import { backupStatoMeta } from '@/lib/statusMaps';
import { useAuthStore } from '@/stores/useAuthStore';
import { RealtimeEvent, isStaffRole } from '@/types';
import type { ConnectionStatus, RealtimeEventName, RealtimeEventPayloads } from '@/types';

/**
 * Subscribes to a single realtime event for the lifetime of the component.
 *
 * The handler is held in a ref so callers can pass an inline closure without
 * re-subscribing on every render — the usual footgun with socket listeners.
 */
export function useRealtimeEvent<E extends RealtimeEventName>(
  event: E,
  handler: (payload: RealtimeEventPayloads[E]) => void,
  enabled = true,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return realtimeService.on(event, (payload) => handlerRef.current(payload));
  }, [event, enabled]);
}

export function useRealtimeStatus(): ConnectionStatus {
  const [status, setStatus] = useState<ConnectionStatus>(realtimeService.getStatus());
  useEffect(() => realtimeService.onStatusChange(setStatus), []);
  return status;
}

/**
 * Opens the shared socket and translates server events into cache updates.
 *
 * Mounted once, inside the authenticated layout. The strategy is deliberate:
 *
 *  - Lists are *invalidated*, not patched. Server-side filtering, sorting and
 *    pagination mean the client cannot know whether a new row belongs on the
 *    page currently displayed, so guessing would show wrong data.
 *  - Detail entries are patched with `setQueryData` when the payload is the
 *    full entity, which makes an open ticket update instantly.
 *  - Notification counters are invalidated so the bell badge stays honest.
 */
export function useRealtimeSync(): ConnectionStatus {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const currentRole = useAuthStore((state) => state.user?.ruolo ?? null);
  const status = useRealtimeStatus();

  useEffect(() => {
    if (!isAuthenticated) return;
    void realtimeService.connect();
    return () => realtimeService.disconnect();
  }, [isAuthenticated]);

  useRealtimeEvent(
    RealtimeEvent.TicketCreated,
    ({ payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // Only announce tickets the current user did not just create, otherwise
      // the author sees a duplicate of their own success toast.
      if (payload.created_by !== currentUserId) {
        toast(`Nuovo ticket: ${payload.oggetto}`);
      }
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.TicketUpdated,
    ({ payload }) => {
      queryClient.setQueryData(queryKeys.tickets.detail(payload.id), payload);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.TicketCommentCreated,
    ({ payload }) => {
      // Defense in depth: internal notes must never refresh a tenant client cache
      // even if a buggy fan-out reaches the tenant room.
      if (payload.interno && (!currentRole || !isStaffRole(currentRole))) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.commenti(payload.ticket_id) });
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.ServiceStateChanged,
    ({ payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.macchine.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      const esito = payload.ultimo_esito?.toLowerCase() ?? '';
      if (esito.includes('error') || esito.includes('fail') || esito.includes('critical')) {
        const macchinaNome = payload.macchina?.nome ?? 'una macchina';
        toast.error(`1 nuova macchina in errore — ${macchinaNome}`);
      }
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.BackupJobCompleted,
    ({ payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      const meta = backupStatoMeta(payload.job_status);
      if (meta.tone === 'error') {
        toast.error(`Backup fallito: ${payload.job_name ?? payload.macchina?.nome ?? 'job sconosciuto'}`);
      }
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.BackupPendingCreated,
    ({ payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.pending.all });
      toast(`Nuovi job Veeam da mappare${payload.cliente_nome ? ` per ${payload.cliente_nome}` : ''}`);
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.BackupPendingResolved,
    () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.pending.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.macchine.all });
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.ServiceEventCreated,
    ({ payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      if (payload.severity === 'critical') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        toast.error(`Evento critico: ${payload.event_type}`);
      }
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.TaskCreated,
    ({ payload }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      if (payload.assegnatari?.some((utente) => utente.id === currentUserId) && payload.created_by !== currentUserId) {
        toast(`Nuova attività assegnata: ${payload.titolo}`);
      }
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.TaskUpdated,
    ({ payload }) => {
      queryClient.setQueryData(queryKeys.tasks.detail(payload.id), payload);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.TaskPaused,
    ({ payload }) => {
      queryClient.setQueryData(queryKeys.tasks.detail(payload.id), payload);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      toast(`Attività in pausa: ${payload.titolo}`);
    },
    isAuthenticated,
  );

  useRealtimeEvent(
    RealtimeEvent.NotificationCreated,
    () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifiche.all });
    },
    isAuthenticated,
  );

  return status;
}
